import { Divider } from 'antd';
import React, { useCallback, useEffect } from 'react';

import { useGlobalState } from '../GlobalState';
import { exotic_fishery } from '../../../common/Utils';
import SummaryLakeMasthead from './SummaryLakeMasthead';
import moment from 'moment';
import { notification } from '../../../../utils/messaging';

import { costString, getSummaryCosts } from '../../../../utils/cost';
import axios from 'axios';
import SvgSummary from '../../assets/images/Summary';
import dayjs from 'dayjs';
import { useGetDynamicBookingTextQuery } from '../../../user_profile/userProfileSlice';

function SideSummaryTimeRow({
  selectedDate,
  isFixedTime,
  selectedTimeAndDate,
  itemStyle,
}) {
  const parsedTime = dayjs(selectedTimeAndDate);
  const parsedDate = dayjs(selectedTimeAndDate || selectedDate);

  return (
    <div className={'justify-between'}>
      <p className={itemStyle}>
        {parsedDate.isValid()
          ? parsedDate.format('MMMM Do YYYY')
          : 'Please select a date'}
      </p>
      {parsedTime.isValid() && (
        <p className={itemStyle}>
          {isFixedTime ? isFixedTime : parsedTime.format('HH:mm a')}
        </p>
      )}
    </div>
  );
}

export default function UserBookingSideSummary({ fisheryId }) {
  const USER_BOOKING_STEP_ORDER = [
    'lake',
    'dates',
    'swims',
    'anglers',
    'summary',
    'payment',
    'success',
    'failure',
  ];
  const { data: DynamicText } = useGetDynamicBookingTextQuery(fisheryId);

  const HideFishery = process.env.REACT_APP_FISHERY_PRICE_HIDE;
  const [bookingDates] = useGlobalState('bookingDates');
  const [userSelectedLakeId] = useGlobalState('userSelectedLakeId');
  const [userSelectedAnglers] = useGlobalState('userSelectedAnglers');
  const [userSelectedRods] = useGlobalState('userSelectedRods');
  const [userSelectedSwims] = useGlobalState('userSelectedSwims');
  const [userSelectedDoubleSwim] = useGlobalState('userSelectedDoubleSwim');
  const [userBookingData, setUserBookingData] =
    useGlobalState('userBookingData');
  const [estimatedPricing, setEstimatedPricing] =
    useGlobalState('estimatedPricing');

  const [guestAllowed] = useGlobalState('guestAllowed');
  const [lakeExclusive] = useGlobalState('lakeExclusive');
  const [lakeDetails] = useGlobalState('lakeDetails');
  const [arrivalTime] = useGlobalState('arrivalTime');
  const [departureTime] = useGlobalState('departureTime');
  const [bookingType] = useGlobalState('bookingType');
  const [addedProductItems] = useGlobalState('addedProductItem');
  const [userSelectedPakage] = useGlobalState('userSelectedPakage');
  const [accommodation] = useGlobalState('accommodation');
  const [lakeType] = useGlobalState('lakeType');
  const [isArrivalFixed, setIsArrivalFixed] = useGlobalState('isArrivalFixed');
  const [isDepartureFixed, setIsDepartureFixed] =
    useGlobalState('isDepartureFixed');
  const is_exotic_fishery = fisheryId === exotic_fishery;
  const is_exotic_arapaima_lake =
    userSelectedLakeId === 'a4688274-3f67-40a5-be8d-a8dc51c053b3';
  const [current, setCurrent] = useGlobalState('current');

  const subTitleStyle = 'gSemiBold mg-bottom-2';
  const summaryTitleStyle = 'gSemiBold mg-bottom-2 fs-18';
  const itemStyle = 'sb-grey2 mg-bottom-10';
  const paddingClass = 'pd-15';

  const hideGuests =
    lakeExclusive || is_exotic_fishery || guestAllowed === 'no';
  const hidePrice = fisheryId === HideFishery;

  const anglersWord =
    is_exotic_fishery && !is_exotic_arapaima_lake ? 'Guest' : 'Angler';

  const {
    total: estimatedTotal,
    advance: estimatedDeposit,
    isDiscounted: isEstimateDiscounted,
    discountedTotal: estimatedDiscountedTotal,
    payable: estimatedPayable,
  } = getSummaryCosts(
    lakeExclusive,
    is_exotic_fishery && !is_exotic_arapaima_lake,
    estimatedPricing
  );
  const estimatedPayableTotal = isEstimateDiscounted
    ? estimatedDiscountedTotal
    : estimatedTotal;

  const anglersCost =
    estimatedPricing?.pricing_details?.pricing_breakdown?.anglers?.reduce(
      (acc, { price }) => acc + price,
      0
    );
  const guestsCost = estimatedTotal - anglersCost;

  const updatePricing = useCallback(() => {
    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };

    if (
      !userSelectedLakeId ||
      !arrivalTime ||
      !departureTime ||
      !userSelectedAnglers ||
      (!(
        lakeType.toLowerCase() === 'ticket' ||
        bookingType === 'ticket' ||
        lakeExclusive
      ) &&
        current !== USER_BOOKING_STEP_ORDER.indexOf('swims') &&
        userSelectedSwims.concat(userSelectedDoubleSwim).length == 0)
    ) {
      return;
    }

    const payload_data = {
      lake_id: userSelectedLakeId,
      arrival_timestamp: arrivalTime,
      departure_timestamp: departureTime,
      num_anglers: userSelectedAnglers,
      num_guest: userSelectedRods,
      swim_ids: userSelectedSwims.concat(userSelectedDoubleSwim),
      angler_details: userBookingData?.angler_details || [],
      booking_type: lakeExclusive ? 'lake_exclusive' : bookingType,
      apply_promotion: true,
      add_on_details: addedProductItems,
      ...(userSelectedPakage && {
        custom_slot_id:
          lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.id,
      }),
    };

    axios
      .post('booking/lake/booking/cost', payload_data, axios_config)
      .then((res) => {
        if (res.status === 200) {
          setEstimatedPricing(res.data.result);
          if (userBookingData?.proceedSummary) {
            setCurrent(USER_BOOKING_STEP_ORDER.indexOf('summary'));
            setUserBookingData({
              ...userBookingData,

              proceedSummary: false,
            });
          }
        }
      })
      .catch((err) => {
        if (err.response) {
          if (
            err.response.status === 401 ||
            (err.response.status === 422 &&
              err.response.data.detail === 'Signature has expired')
          ) {
            notification.error({
              message: <div className='gSemiBold'>There was an issue!</div>,
              style: { border: '1px #D34545 solid' },
              placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
              className: 'gRegular br10',
              description: 'Please login to continue',
            });
          } else if (err.response.status === 400) {
            if (err.response.data.detail) {
              notification.error({
                description: err.response.data.detail,
              });
            }
          }
        }
      })
      .finally(() => {});
  }, [
    addedProductItems,
    arrivalTime,
    bookingType,
    departureTime,
    lakeDetails?.custom_slot_days,
    lakeExclusive,
    setEstimatedPricing,
    userBookingData?.angler_details,
    userSelectedAnglers,
    userSelectedDoubleSwim,
    userSelectedLakeId,
    userSelectedPakage,
    userSelectedRods,
    userSelectedSwims,
  ]);

  useEffect(() => {
    updatePricing();
  }, [updatePricing]);

  let totalHelpText;
  if (accommodation) {
    totalHelpText = '';
  } else if (DynamicText?.is_active) {
    totalHelpText =
      DynamicText?.properties?.booking_step_1?.lake_exclusive_label;
  } else if (lakeExclusive) {
    totalHelpText = `For Up To ${
      lakeDetails?.lake_exclusive_anglers_allowed ?? '10'
    }  Anglers`;
  } else {
    totalHelpText = `For ${userSelectedAnglers} ${anglersWord}${
      userSelectedAnglers !== 1 ? 's' : ''
    }`;
    if (!hideGuests) {
      totalHelpText += ` & ${userSelectedRods} Guest${
        userSelectedRods !== 1 ? 's' : ''
      }`;
    }
  }

  return (
    <div className='br10 overflow-hidden gRegular fs-16 border-1 border-grey'>
      <h3
        className={
          paddingClass +
          ' gSemiBold fs-24 sb-blue-bg white w-full justify-center row-center gap-05r'
        }
      >
        <SvgSummary className={'mg-bottom-2'} />
        <span>Booking Summary</span>
      </h3>
      <div className={paddingClass + ' pd-top-5 pd-bottom-5'}>
        <SummaryLakeMasthead className={'mg-10 br10'} />

        <Divider className={'mg-bottom-20'} />

        <p className={subTitleStyle}>Arrival:</p>
        <SideSummaryTimeRow
          selectedDate={bookingDates?.[0]?.startDate}
          selectedTimeAndDate={arrivalTime}
          isFixedTime={isArrivalFixed}
          itemStyle={itemStyle}
        />
        <p className={subTitleStyle}>Departure:</p>
        <SideSummaryTimeRow
          selectedDate={bookingDates?.[0]?.endDate}
          isFixedTime={isDepartureFixed}
          selectedTimeAndDate={departureTime}
          itemStyle={itemStyle}
        />
        {!accommodation && (
          <>
            <Divider className={'mg-top-10'} />

            <p className={subTitleStyle}>Details:</p>
            {lakeExclusive && (
              <div className={'justify-between'}>
                <p className={itemStyle}>
                  {DynamicText?.is_active &&
                  DynamicText?.properties?.booking_step_1
                    ?.booking_summary_booking_type
                    ? DynamicText?.properties?.booking_step_1
                        ?.booking_summary_booking_type
                    : 'Lake Exclusive'}
                </p>
                <p className={itemStyle}>{costString(estimatedTotal)}</p>
              </div>
            )}
            {!lakeExclusive && (
              <div className={'justify-between'}>
                <p className={itemStyle}>
                  {userSelectedAnglers} x {anglersWord}
                  {userSelectedAnglers !== 1 ? 's' : ''}
                </p>
                <p className={itemStyle}>{costString(anglersCost)}</p>
              </div>
            )}
            {!hideGuests && (
              <div className={'justify-between'}>
                <p className={itemStyle}>
                  {userSelectedRods} x Guest{userSelectedRods !== 1 ? 's' : ''}
                </p>
                <p className={itemStyle}>{costString(guestsCost)}</p>
              </div>
            )}
          </>
        )}
      </div>
      {!hidePrice && (
        <div
          className={
            paddingClass +
            ' sb-light-grey-bg pd-top-30 border-top-1 border-grey'
          }
        >
          <div className={'justify-between'}>
            <p className={summaryTitleStyle}>Total:</p>
            <p
              className={
                summaryTitleStyle +
                (isEstimateDiscounted ? ' txt-strikethrough pd-left-5 ' : ' ')
              }
            >
              {costString(estimatedTotal)}
            </p>
          </div>
          {isEstimateDiscounted && (
            <div className={'justify-between'}>
              <p className='sb-blue-bg br5 fs-16 gSemiBold txt-center white pd-2 pd-left-15 pd-right-15 mg-top-4 mg-bottom-4'>
                Promotion Applied:
              </p>
              <p className={summaryTitleStyle}>
                {costString(estimatedDiscountedTotal)}
              </p>
            </div>
          )}
          {Boolean(estimatedDeposit) &&
            estimatedDeposit < estimatedPayableTotal && (
              <div className={'justify-between'}>
                <p className={summaryTitleStyle}>Deposit:</p>
                <p className={summaryTitleStyle}>
                  {costString(estimatedDeposit)}
                </p>
              </div>
            )}
          <p className={itemStyle + ' fs-14'}>{totalHelpText}</p>
        </div>
      )}
    </div>
  );
}
